/**
 * Display rounding for the Overview. Statistics keep full precision in the
 * pure module; rounding happens only here, at render.
 */

/** Below this many eligible responses, cards show counts instead of percentages. */
export const PERCENT_MIN_ELIGIBLE = 10;

const percent = new Intl.NumberFormat('en', { style: 'percent', maximumFractionDigits: 0 });
const oneDecimal = new Intl.NumberFormat('en', { maximumFractionDigits: 1 });
const integer = new Intl.NumberFormat('en', { maximumFractionDigits: 0 });

/** A share as a whole-number percentage. */
export function formatPercent(share: number): string {
  return percent.format(share);
}

/** A bar label: "count · %", or "count of eligible" below 10 eligible responses. */
export function formatCount(count: number, answered: number, eligible: number): string {
  if (eligible < PERCENT_MIN_ELIGIBLE) return `${integer.format(count)} of ${integer.format(eligible)}`;
  return `${integer.format(count)} · ${formatPercent(answered ? count / answered : 0)}`;
}

/** A mean, median or bound: integers stay whole, everything else gets one decimal. */
export function formatStat(value: number | null): string {
  if (value === null) return '';
  return Number.isInteger(value) ? integer.format(value) : oneDecimal.format(value);
}

const compact = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });

/** A number shortened for narrow labels (1K, 2.5K, 1.2M). */
export function formatCompact(value: number): string {
  return compact.format(value);
}

/** A correlation coefficient to two decimals. */
export function formatCorrelation(value: number | null): string {
  return value === null ? '' : value.toFixed(2);
}

/** Minutes to one decimal. */
export function formatMinutes(value: number | null): string {
  return value === null ? '' : `${value.toFixed(1)} min`;
}
